import { defineComponent, inject, nextTick, onMounted, ref, Ref, watch, computed } from 'vue';
import ClientService from '@/sections/admin/entities/client/client.service';
import { Client } from '@/shared/model/client.model';
import { useStore } from '@/plugins/vuex';

export default defineComponent({
  props: {
    defaultClientId: {
      type: String,
      required: false
    }
  },
  setup(props) {
    const store = useStore();
    const clientService = inject('clientService') as () => ClientService;
    const clients: Ref<Client[]> = ref([]);
    const selectedItem = ref<Client>(store.getters['cartStore/clientSelected']);
    const isDisabled = ref(false);
    const clientAutocomplete = ref(null);
    const labelDisplay = ref('');

    onMounted(async () => {
      await retrieveAllClients();
      if (props.defaultClientId) {
        setDefaultClient(clients.value.find(client => client.id === props.defaultClientId));
        return;
      }
    });

    watch(
      () => props.defaultClientId,
      newValue => {
        setDefaultClient(clients.value.find(client => client.id === props.defaultClientId));
      }
    );

    const setDefaultClient = (client: Client) => {
      isDisabled.value = true;
      selectedItem.value = client;
      if (client) store.commit('cartStore/setClient', client);
    };

    const retrieveAllClients = async () => {
      try {
        const response = await clientService().retrieveWithRates();
        const processedClients = response.data.map(client => ({
          ...client,
          company: client.company || { name: '' }
        }));
        clients.value = sortClientsByCompany(processedClients);
      } catch (error) {
        console.error('Error retrieving clients:', error);
      }
    };

    const sortClientsByCompany = (clients: Client[]) => {
      return clients.sort((a, b) => {
        const companyA = a.company.name || '';
        const companyB = b.company.name || '';

        if (!companyA) return 1;
        if (!companyB) return -1;

        return companyA.localeCompare(companyB);
      });
    };

    const groupedClients = computed(() => clients.value);

    const onClientSelected = (client: Client) => {
      store.commit('cartStore/setClient', client);
    };

    const scrollToStart = () => {
      const input = clientAutocomplete.value.$el.querySelector('input');
      if (input) {
        input.scrollLeft = 0;
      }
    };

    const isItemSelected = item => {
      setLabelDisplay();
      if (!selectedItem.value) return false;
      return selectedItem.value.id === item.id;
    };

    const setLabelDisplay = () => {
      const itemDesc = selectedItem.value?.name;
      labelDisplay.value = `${itemDesc}`;
    };

    const formatItemName = (item: Client) => {
      return `${item.name} ${item.surname.split(' ')[0]}`;
    };

    const formatItemText = (item: Client) => {
      const name = formatItemName(item);
      const company = item.company?.name ? ` - ${item.company.name}` : '';
      return `${name}${company}`;
    };

    return {
      clientAutocomplete,
      selectedItem,
      clients,
      groupedClients,
      isDisabled,
      onClientSelected,
      scrollToStart,
      isItemSelected,
      formatItemText,
      formatItemName
    };
  }
});
